export type FrontendErrorCategory =
  | "route_asset_load"
  | "stack_overflow"
  | "chart_render"
  | "auth"
  | "storage"
  | "network"
  | "permission"
  | "api"
  | "render"
  | "resource_load"
  | "unknown";

export type FrontendErrorSeverity = "recoverable" | "warning" | "error" | "fatal";

export type FrontendErrorDiagnostic = {
  category: FrontendErrorCategory;
  code: string;
  severity: FrontendErrorSeverity;
  title: string;
  userMessage: string;
  technicalMessage: string;
  likelyCause: string;
  recommendedAction: string;
};

type FrontendErrorDiagnosticInput = {
  source?: string;
  componentStack?: string;
  extra?: Record<string, unknown>;
};

const ROUTE_ASSET_PATTERNS = [
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "importing a module script failed",
  "is not a valid javascript mime type",
  "expected a javascript module script but the server responded with a mime type",
  "loading chunk",
  "chunkloaderror",
];

function stringValue(value: unknown) {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (value instanceof Error) return `${value.name}: ${value.message}\n${value.stack || ""}`;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function errorName(error: unknown) {
  return error instanceof Error ? error.name || "Error" : "Error";
}

function errorMessage(error: unknown) {
  if (error instanceof Error) return error.message || "Unknown frontend error";
  const text = stringValue(error).trim();
  return text || "Unknown frontend error";
}

function textIncludesAny(text: string, patterns: string[]) {
  const lower = text.toLowerCase();
  return patterns.some((pattern) => lower.includes(pattern));
}

function diagnostic(
  category: FrontendErrorCategory,
  code: string,
  severity: FrontendErrorSeverity,
  title: string,
  userMessage: string,
  technicalMessage: string,
  likelyCause: string,
  recommendedAction: string,
): FrontendErrorDiagnostic {
  return { category, code, severity, title, userMessage, technicalMessage, likelyCause, recommendedAction };
}

export function isRouteAssetLoadError(error: unknown) {
  return textIncludesAny(stringValue(error), ROUTE_ASSET_PATTERNS);
}

export function classifyFrontendError(
  error: unknown,
  input: FrontendErrorDiagnosticInput = {},
): FrontendErrorDiagnostic {
  const text = [
    errorName(error),
    errorMessage(error),
    error instanceof Error ? error.stack || "" : "",
    input.componentStack || "",
    stringValue(input.extra),
  ].join("\n");
  const lower = text.toLowerCase();
  const technicalMessage = errorMessage(error);

  if (input.extra?.route_asset_load_error === true || isRouteAssetLoadError(error)) {
    return diagnostic(
      "route_asset_load",
      "FE_ROUTE_ASSET_LOAD",
      "recoverable",
      "App update required",
      "A JavaScript bundle for this route failed to load. The app will reload with the latest assets.",
      technicalMessage,
      "A browser tab was using an older deployment while a route chunk changed or expired.",
      "Reload the page; if it repeats, inspect the chunk URL and deployment alias in the frontend error log.",
    );
  }

  if (lower.includes("maximum call stack size exceeded") || lower.includes("too much recursion")) {
    return diagnostic(
      "stack_overflow",
      "FE_STACK_OVERFLOW",
      "fatal",
      "Recursive UI state detected",
      "The page hit a recursive render or data-mapping loop.",
      technicalMessage,
      "A component, route mapper, or derived value called back into itself until the browser stack overflowed.",
      "Use the component stack and mapped source stack to find the recursive function pair.",
    );
  }

  if (
    lower.includes("recharts") ||
    lower.includes("<areachart>") ||
    lower.includes("getticks") ||
    lower.includes("getnicetickvalues") ||
    lower.includes("decimal2")
  ) {
    return diagnostic(
      "chart_render",
      "FE_CHART_RENDER",
      "error",
      "Chart rendering failed",
      "A chart could not render one of its data series.",
      technicalMessage,
      "Chart data, axis domain, or tick generation produced an invalid render state.",
      "Inspect chart input values, computed axis domains, and the Recharts stack frames.",
    );
  }

  if (
    lower.includes("failed to fetch") ||
    lower.includes("networkerror") ||
    lower.includes("err_network") ||
    lower.includes("err_timeout") ||
    lower.includes("load failed")
  ) {
    return diagnostic(
      "network",
      "FE_NETWORK",
      "warning",
      "Network request failed",
      "The page could not reach a required service.",
      technicalMessage,
      "The browser could not complete a fetch, API call, or asset request.",
      "Check the request URL, status, backend availability, CORS, and deployment rewrites.",
    );
  }

  if (
    lower.includes("auth0") ||
    lower.includes("oauth") ||
    lower.includes("redirect_uri") ||
    lower.includes("unauthorized") ||
    lower.includes("token")
  ) {
    return diagnostic(
      "auth",
      "FE_AUTH",
      "error",
      "Sign-in flow failed",
      "The authentication flow could not complete.",
      technicalMessage,
      "The Auth0/OAuth/session state is invalid, expired, or misconfigured for this origin.",
      "Check redirect URIs, session state, hosted auth response, and backend auth logs.",
    );
  }

  if (
    lower.includes("localstorage") ||
    lower.includes("sessionstorage") ||
    lower.includes("quotaexceeded") ||
    lower.includes("storage")
  ) {
    return diagnostic(
      "storage",
      "FE_BROWSER_STORAGE",
      "error",
      "Browser storage failed",
      "The page could not read or write required browser state.",
      technicalMessage,
      "Local/session storage may be blocked, full, private-mode restricted, or holding malformed data.",
      "Inspect storage availability and the scoped wallet/auth keys included in the frontend error log.",
    );
  }

  if (
    lower.includes("notallowederror") ||
    lower.includes("permission denied") ||
    lower.includes("securityerror")
  ) {
    return diagnostic(
      "permission",
      "FE_BROWSER_PERMISSION",
      "error",
      "Browser permission blocked",
      "The browser blocked an operation required by this page.",
      technicalMessage,
      "A browser permission, security policy, iframe policy, or clipboard/download rule blocked the action.",
      "Inspect browser policy state, CSP, and the failing API call in the stack trace.",
    );
  }

  if (lower.includes("apierror") || lower.includes("err_")) {
    return diagnostic(
      "api",
      "FE_API",
      "error",
      "Backend request failed",
      "A backend response could not be handled by the page.",
      technicalMessage,
      "The backend returned an error payload, timeout, or malformed response.",
      "Match the frontend error ID with backend request logs and inspect error_code/error_detail.",
    );
  }

  if (input.source === "resource_error") {
    return diagnostic(
      "resource_load",
      "FE_RESOURCE_LOAD",
      "warning",
      "Page resource failed to load",
      "A script, stylesheet, image, or other browser resource failed to load.",
      technicalMessage,
      "The browser could not load a page resource referenced by the current deployment.",
      "Inspect the resource URL, HTTP status, CSP, and deployment asset list.",
    );
  }

  if (input.componentStack || input.source === "react_boundary" || input.source === "router_boundary") {
    return diagnostic(
      "render",
      "FE_RENDER",
      "fatal",
      "Page rendering failed",
      "A route or React component threw while rendering.",
      technicalMessage,
      "A component, hook, or route loader threw during render or route resolution.",
      "Use the source-mapped stack, component stack, and route path in the frontend error log.",
    );
  }

  return diagnostic(
    "unknown",
    "FE_UNKNOWN",
    "error",
    "Unexpected frontend error",
    "The page hit an unexpected browser error.",
    technicalMessage,
    "The error did not match a known diagnostic category yet.",
    "Inspect the source-mapped stack, browser context, and captured assets.",
  );
}
